import { useCallback, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  Alert,
  Box,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Link from "@mui/material/Link";

import { IEventoResponse } from "interfaces/eventos";
import { formatCurrency } from "helpers/utils";
import { resolverCentroCustoIdEvento } from "layouts/pages/loja/financeiro/dashboard/helpers/resolver-centro-custo-evento";
import { useCentrosCusto } from "layouts/pages/loja/financeiro/dashboard/hooks/useCentrosCusto";
import { useContasPagar } from "layouts/pages/loja/financeiro/dashboard/hooks/useContasPagar";
import { useFornecedoresFinanceiro } from "layouts/pages/loja/financeiro/dashboard/hooks/useFornecedoresFinanceiro";
import { useContasFinanceirasFinanceiro } from "layouts/pages/loja/financeiro/dashboard/hooks/useContasFinanceirasFinanceiro";
import { useBaixaTitulo } from "layouts/pages/loja/financeiro/dashboard/hooks/useBaixaTitulo";
import { useCancelamentoTitulo } from "layouts/pages/loja/financeiro/dashboard/hooks/useCancelamentoTitulo";
import { useCriarTituloPagar } from "layouts/pages/loja/financeiro/dashboard/hooks/useCriarTituloPagar";
import { useEditarTituloPagar } from "layouts/pages/loja/financeiro/dashboard/hooks/useEditarTituloPagar";
import ContasPagarSection from "layouts/pages/loja/financeiro/dashboard/components/ContasPagarSection";
import BaixaTituloDialog from "layouts/pages/loja/financeiro/dashboard/components/BaixaTituloDialog";
import CancelamentoTituloDialog from "layouts/pages/loja/financeiro/dashboard/components/CancelamentoTituloDialog";
import NovaContaPagarDialog from "layouts/pages/loja/financeiro/dashboard/components/NovaContaPagarDialog";

import {
  DESPESAS_ACCENT,
  despesasSectionSx,
  despesasToolbarSx,
} from "./despesas-evento.theme";

interface Props {
  evento: IEventoResponse;
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={accent ? DESPESAS_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function DespesasEvento({ evento }: Props) {
  const contasPagar = useContasPagar({ eventoId: evento.id });
  const centros = useCentrosCusto({ ativo: true, somenteEventosAtivos: true });
  const fornecedoresFinanceiro = useFornecedoresFinanceiro();
  const contasFinanceirasFinanceiro = useContasFinanceirasFinanceiro();

  const handleMutacaoSucesso = useCallback(() => {
    contasPagar.refetch();
    fornecedoresFinanceiro.refetch();
  }, [contasPagar, fornecedoresFinanceiro]);

  const criarTitulo = useCriarTituloPagar({ onSuccess: handleMutacaoSucesso });
  const editarTitulo = useEditarTituloPagar({ onSuccess: handleMutacaoSucesso });
  const baixaTitulo = useBaixaTitulo({
    onSuccess: handleMutacaoSucesso,
    contasFinanceiras: contasFinanceirasFinanceiro.data,
  });
  const cancelamentoTitulo = useCancelamentoTitulo({
    onSuccess: handleMutacaoSucesso,
  });
  const [abrindoNovaDespesa, setAbrindoNovaDespesa] = useState(false);

  const abrirNovaDespesa = useCallback(async () => {
    setAbrindoNovaDespesa(true);
    try {
      const centroCustoId = await resolverCentroCustoIdEvento(
        evento.id,
        centros.data,
      );
      if (centroCustoId) {
        void centros.refetch();
      }
      criarTitulo.abrir({
        contexto: "EVENTO",
        eventoId: evento.id,
        categoria: "SERVICOS",
        descricao: evento.titulo ? `Despesa — ${evento.titulo}` : "",
        centroCustoId,
      });
    } finally {
      setAbrindoNovaDespesa(false);
    }
  }, [centros, criarTitulo, evento.id, evento.titulo]);

  const financeiroLink = `/loja/financeiro/contas-pagar?${new URLSearchParams({
    novaDespesaEvento: String(evento.id),
  }).toString()}`;

  const semPermissao =
    contasPagar.error === "Sem permissão para listar contas a pagar.";

  const resumo = contasPagar.data?.resumo;
  const totalRegistros = contasPagar.data?.total ?? 0;

  return (
    <MDBox>
      <Box sx={despesasToolbarSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ReceiptLongOutlinedIcon sx={{ color: DESPESAS_ACCENT }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Despesas do evento
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {evento.titulo}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <MDButton
              variant="gradient"
              color="dark"
              disabled={abrindoNovaDespesa || semPermissao}
              onClick={() => void abrirNovaDespesa()}
              startIcon={<AddOutlinedIcon />}
              sx={{
                flexShrink: 0,
                background: `linear-gradient(195deg, ${DESPESAS_ACCENT}, #e04a1a)`,
              }}
            >
              {abrindoNovaDespesa ? "Abrindo…" : "Nova despesa"}
            </MDButton>
            <MDButton
              component={RouterLink}
              to={financeiroLink}
              state={{ eventoNome: evento.titulo }}
              variant="outlined"
              color="dark"
              startIcon={<OpenInNewOutlinedIcon />}
              sx={{ flexShrink: 0 }}
            >
              Abrir no financeiro
            </MDButton>
          </Stack>
        </Stack>
      </Box>

      {semPermissao ? (
        <Box sx={despesasSectionSx}>
          <Alert severity="warning">
            Você não tem permissão para visualizar despesas financeiras. Peça
            acesso ao módulo financeiro ou use{" "}
            <Link component={RouterLink} to={financeiroLink}>
              Contas a pagar
            </Link>
            .
          </Alert>
        </Box>
      ) : (
        <>
          {criarTitulo.feedback ? (
            <Alert severity={criarTitulo.feedback.severity} sx={{ mb: 2 }}>
              {criarTitulo.feedback.message}
            </Alert>
          ) : null}

          {contasPagar.loading && !contasPagar.data ? (
            <Grid container spacing={2} mb={2.5}>
              {[1, 2, 3].map((i) => (
                <Grid item xs={6} sm={4} key={i}>
                  <Skeleton variant="rounded" height={88} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={2} mb={2.5}>
              <Grid item xs={6} sm={4}>
                <StatCard
                  label="Total de despesas"
                  value={totalRegistros}
                  accent
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <StatCard
                  label="Em aberto"
                  value={formatCurrency(resumo?.totalAberto ?? 0)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  label="Pagos / vencidos"
                  value={`${formatCurrency(resumo?.totalPago ?? 0)} · ${resumo?.quantidadeVencido ?? 0} venc.`}
                />
              </Grid>
            </Grid>
          )}

          {!contasPagar.loading &&
          contasPagar.isEmpty &&
          !contasPagar.isFiltered ? (
            <Box
              sx={{
                py: 6,
                px: 2,
                textAlign: "center",
                borderRadius: 2,
                border: "1px dashed",
                borderColor: "grey.300",
                bgcolor: "grey.50",
              }}
            >
              <ReceiptLongOutlinedIcon
                sx={{ fontSize: 44, color: "grey.400", mb: 1 }}
              />
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Nenhuma despesa cadastrada
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Registre títulos a pagar vinculados a este evento.
              </Typography>
              <MDButton
                variant="outlined"
                color="dark"
                onClick={() => void abrirNovaDespesa()}
                startIcon={<AddOutlinedIcon />}
                disabled={abrindoNovaDespesa}
              >
                Adicionar primeira despesa
              </MDButton>
            </Box>
          ) : (
            <ContasPagarSection
              contasPagar={contasPagar}
              centros={centros}
              fornecedores={fornecedoresFinanceiro}
              ocultarFiltros
              ocultarCards
              tabelaVariant="evento"
              tabelaEmbedded
              onBaixarTitulo={
                baixaTitulo.podeBaixaManual
                  ? (titulo) =>
                      baixaTitulo.abrirBaixaPagar(
                        titulo,
                        contasFinanceirasFinanceiro.data,
                      )
                  : undefined
              }
              onCancelarTitulo={
                cancelamentoTitulo.podeCancelarTitulo
                  ? cancelamentoTitulo.abrirCancelamentoPagar
                  : undefined
              }
              onEditarTitulo={
                editarTitulo.podeEditar ? editarTitulo.abrir : undefined
              }
            />
          )}
        </>
      )}

      <NovaContaPagarDialog
        open={criarTitulo.open}
        form={criarTitulo.form}
        setForm={criarTitulo.setForm}
        loading={criarTitulo.loading}
        error={criarTitulo.error}
        centros={centros.data}
        fornecedores={fornecedoresFinanceiro.data}
        onRefetchFornecedores={fornecedoresFinanceiro.refetch}
        onClose={criarTitulo.fechar}
        onConfirm={() => void criarTitulo.confirmar()}
        variant="create"
        eventoHeader={{
          titulo: evento.titulo,
          urlImageDestaque: evento.urlImageDestaque,
        }}
      />

      <NovaContaPagarDialog
        open={editarTitulo.open}
        form={editarTitulo.form}
        setForm={editarTitulo.setForm}
        loading={editarTitulo.loading}
        error={editarTitulo.error}
        centros={centros.data}
        fornecedores={fornecedoresFinanceiro.data}
        onRefetchFornecedores={fornecedoresFinanceiro.refetch}
        onClose={editarTitulo.fechar}
        onConfirm={() => void editarTitulo.confirmar()}
        variant="edit"
        eventoHeader={{
          titulo: evento.titulo,
          urlImageDestaque: evento.urlImageDestaque,
        }}
      />

      <BaixaTituloDialog
        dialog={baixaTitulo.dialog}
        form={baixaTitulo.form}
        setForm={baixaTitulo.setForm}
        contasFinanceiras={contasFinanceirasFinanceiro.data}
        contasLoading={contasFinanceirasFinanceiro.loading}
        contasError={contasFinanceirasFinanceiro.error}
        loading={baixaTitulo.loading}
        error={baixaTitulo.error}
        onClose={baixaTitulo.fecharDialog}
        onConfirm={baixaTitulo.confirmarBaixa}
      />

      <CancelamentoTituloDialog
        dialog={cancelamentoTitulo.dialog}
        form={cancelamentoTitulo.form}
        setForm={cancelamentoTitulo.setForm}
        loading={cancelamentoTitulo.loading}
        error={cancelamentoTitulo.error}
        onClose={cancelamentoTitulo.fecharDialog}
        onConfirm={cancelamentoTitulo.confirmarCancelamento}
      />
    </MDBox>
  );
}
